
import { supabase } from '@/integrations/supabase/client';
import { extractSpotifyTrackId } from '@/lib/spotify';

/**
 * Sanitizes a filename by removing special characters that could cause issues
 */
function sanitizeFileName(fileName: string): string {
  // Remove special characters entirely instead of replacing with underscores
  return fileName
    .replace(/[^\w\s.-]/g, '') // Remove any non-alphanumeric, non-space, non-dot, non-dash
    .replace(/\s+/g, '_'); // Replace spaces with underscores
}

/**
 * Ensures the uploaded file has the correct name and path structure
 */
function createUploadPath(artistId: string, fileName: string): string {
  // Ensure artist id is trimmed and sanitized
  const safeArtistId = artistId.trim();
  
  // Sanitize the filename to prevent issues
  const sanitizedFileName = sanitizeFileName(fileName);
  
  // Create a path with artist id as the folder
  return `${safeArtistId}/${sanitizedFileName}`;
}

export async function uploadFileToStorage(
  spotifyUrl: string,
  selectedFile: File,
  artistId: string
): Promise<{ filePath: string; publicUrl: string; error?: string } | null> {
  try {
    console.log("Starting file upload process for:", selectedFile.name);
    console.log("Artist ID:", artistId);
    
    const trackId = extractSpotifyTrackId(spotifyUrl);
    if (!trackId) {
      console.error("Failed to extract track ID from URL:", spotifyUrl);
      return { filePath: '', publicUrl: '', error: "Couldn't extract track ID from Spotify URL" };
    }
    
    console.log("Track ID extracted:", trackId);
    
    // Create a unique path for this file
    const uniquePath = createUploadPath(artistId, selectedFile.name);
    
    console.log("File will be stored at path:", uniquePath);
    
    // First ensure we have auth session
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.error("No authentication session found for upload");
      return { filePath: '', publicUrl: '', error: "Authentication required for upload" };
    }
    
    // Use the 'uploads' bucket which is now correctly configured via SQL
    const bucketName = 'uploads';
    
    console.log(`Uploading file of size: ${selectedFile.size} bytes to bucket: ${bucketName}`);
    console.log(`User ID from session: ${session.user.id}`);
    console.log(`Artist ID used for path: ${artistId}`);
    
    // Get content type from file or fallback to generic binary type
    let contentType = selectedFile.type;
    if (!contentType || contentType === 'application/octet-stream') {
      // Use generic binary content type if specific type can't be determined
      contentType = 'application/octet-stream';
    }
    
    console.log(`Using content type: ${contentType} for file: ${selectedFile.name}`);
    
    // Upload file to Supabase Storage (now with proper policies)
    const { data: fileData, error: uploadError } = await supabase.storage
      .from(bucketName)
      .upload(uniquePath, selectedFile, {
        cacheControl: '3600',
        upsert: true, // Allow overwriting files
        contentType: contentType // Explicitly set content type
      });
    
    if (uploadError) {
      console.error("Upload error details:", uploadError);
      return { filePath: '', publicUrl: '', error: uploadError.message || "Failed to upload file" };
    }
    
    // Get the public URL for the uploaded file
    const { data: { publicUrl } } = supabase.storage
      .from(bucketName)
      .getPublicUrl(uniquePath);
    
    console.log("File uploaded successfully:", { filePath: uniquePath, publicUrl });
    return { filePath: uniquePath, publicUrl };
  } catch (error: any) {
    console.error("File upload error:", error);
    return { filePath: '', publicUrl: '', error: error.message || "An unexpected error occurred during upload" };
  }
}
